import { useTranslation } from "react-i18next";
import { Button, Layer, Tile } from "@carbon/react";
import { OfferAssistantRecommendation } from "../../types";
import {
  hasSingleSupplierAlternative,
  recommendedOfferIds,
} from "./offerAssistantUtils";

type Props = {
  recommendation: OfferAssistantRecommendation;
  /** Pre-select these offers in the award matrix (never awards). */
  onApply: (offerIds: string[]) => void;
  onDismiss: () => void;
};

/**
 * Prices come computed server-side (landed unit price / whole-award total, in the order's
 * currency): shown as-is, only formatted. Falls back to the raw number if the currency code is
 * not one Intl knows.
 */
export const formatAmount = (
  value: number | null,
  currency: string,
  fallback: string
): string => {
  if (value == null) return fallback;
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "ARS",
      currencyDisplay: "code",
    }).format(value);
  } catch {
    return `${value} ${currency ?? ""}`.trim();
  }
};

const price = formatAmount;

/** Compact summary of the active recommendation, with its apply / dismiss actions. */
const OfferAssistantRecommendationCard = ({
  recommendation,
  onApply,
  onDismiss,
}: Props) => {
  const { t } = useTranslation();
  const single = recommendation.bestSingleSupplier;
  const noPrice = t("offerAssistant.recommendation.noPrice");

  return (
    <Layer>
      <Tile
        className="offer-assistant__rec"
        aria-label={t("offerAssistant.recommendation.title")}
      >
        <h4 className="offer-assistant__rec-title">
          {t("offerAssistant.recommendation.title")}
        </h4>

        {recommendation.items.length > 0 && (
          <ul className="offer-assistant__rec-items">
            {recommendation.items.map((item) => (
              <li key={item.offerId} className="offer-assistant__rec-item">
                <div className="offer-assistant__rec-line">
                  <strong>{item.materialName}</strong>
                  <span>{item.supplierName}</span>
                  <span className="offer-assistant__rec-price">
                    {price(item.normalizedPrice, item.currency, noPrice)}
                  </span>
                </div>
                <p className="offer-assistant__rec-reason">{item.reason}</p>
              </li>
            ))}
          </ul>
        )}

        {single && (
          <div className="offer-assistant__rec-single">
            <div className="offer-assistant__rec-line">
              <strong>
                {t("offerAssistant.recommendation.bestSingleSupplier")}
              </strong>
              <span>{single.supplierName}</span>
              <span className="offer-assistant__rec-price">
                {price(single.total, single.currency, noPrice)}
              </span>
            </div>
            <p className="offer-assistant__rec-reason">{single.reason}</p>
          </div>
        )}

        <p className="offer-assistant__rec-note">
          {t("offerAssistant.recommendation.note")}
        </p>

        <div className="offer-assistant__rec-actions">
          {recommendedOfferIds(recommendation).length > 0 && (
            <Button
              size="sm"
              onClick={() => onApply(recommendedOfferIds(recommendation))}
            >
              {t("offerAssistant.recommendation.apply")}
            </Button>
          )}
          {single && hasSingleSupplierAlternative(recommendation) && (
            <Button
              size="sm"
              kind="tertiary"
              onClick={() => onApply(single.offerIds)}
            >
              {t("offerAssistant.recommendation.applySingle")}
            </Button>
          )}
          <Button size="sm" kind="ghost" onClick={onDismiss}>
            {t("offerAssistant.recommendation.dismiss")}
          </Button>
        </div>
      </Tile>
    </Layer>
  );
};

export default OfferAssistantRecommendationCard;
